/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import EditPostMixin from '@linc-technologies/linced-common/mixins/edit-post';
import { action, computed } from '@ember/object';
import moment from 'moment';
import { service } from '@ember/service';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import { filter, setDiff, alias } from '@ember/object/computed';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class IndexController extends Controller.extend(EditPostMixin) {
  // Services
  @service
  router;

  @service
  session;

  @service
  store;

  @service
  terms;

  @service
  toaster;

  PostStatus = PostStatus;
  @tracked
  postsLoading = false;

  queryParams = [
    'proofreadPosts',
    'notProofread',
    'draftPosts',
    'publishedPosts',
    'staffOnly',
    'selectedPageIds',
    'selectedAuthorIds',
    'startDate',
    'endDate',
  ];

  @tracked
  proofreadPosts = false;
  @tracked
  notProofread = false;
  @tracked
  draftPosts = false;
  @tracked
  publishedPosts = false;
  @tracked
  staffOnly = false;
  @tracked startDate = moment().startOf('year').format('YYYY-MM-DD');
  @tracked endDate = moment().endOf('year').format('YYYY-MM-DD');

  @computed('startDate', 'endDate', 'terms.currentTerm.{start,end}')
  get dateRange() {
    if (
      this.startDate === moment().format('YYYY-MM-DD') &&
      this.endDate === moment().format('YYYY-MM-DD')
    ) {
      return 'day';
    }
    if (
      this.startDate ===
        moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD') &&
      this.endDate === moment().endOf('day').format('YYYY-MM-DD')
    ) {
      return 'week';
    }
    let term = this.get('terms.currentTerm');
    if (term) {
      if (this.startDate === term.start && this.endDate === term.end) {
        return 'term';
      }
    }
  }

  @alias('model.group.id')
  groupId;

  @filter('posts', function (post) {
    if (this.staffOnly) {
      return false;
    }
    return [PostStatus.DRAFT, PostStatus.UNPUBLISHED].includes(post.status);
  })
  filteredDraftPosts;

  @computed('model.pages.@each.{sequence,label}')
  get pages() {
    return this.model.pages
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @setDiff('model.pages', 'selectedPages')
  remainingPages;

  @computed('remainingPages.[]')
  get sortedRemainingPages() {
    return this.remainingPages
      .slice()
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }

  @computed('selectedPageIds.[]')
  get selectedPages() {
    return this.selectedPageIds.map((pageId) => {
      return this.store.peekRecord('posts/page', pageId);
    });
  }

  @tracked
  selectedPageIds = [];

  @computed('terms.currentTerm')
  get termDates() {
    let term = this.get('terms.currentTerm');
    if (term) {
      return {
        startDate: term.start,
        endDate: term.end,
      };
    }
    return null;
  }

  @computed('model.authors.@each.{lastName,firstName}')
  get authors() {
    return this.model.authors
      .slice()
      .sort(
        (a, b) =>
          (a.lastName || '').localeCompare(b.lastName || '') ||
          (a.firstName || '').localeCompare(b.firstName || ''),
      );
  }

  @setDiff('model.authors', 'selectedAuthors')
  remainingAuthors;

  @computed('remainingAuthors.[]')
  get sortedRemainingAuthors() {
    return this.remainingAuthors
      .slice()
      .sort(
        (a, b) =>
          (a.lastName || '').localeCompare(b.lastName || '') ||
          (a.firstName || '').localeCompare(b.firstName || ''),
      );
  }

  @computed('selectedAuthorIds.[]')
  get selectedAuthors() {
    return this.selectedAuthorIds.map((authorId) => {
      return this.store.peekRecord('person', authorId);
    });
  }

  @tracked
  selectedAuthorIds = [];

  @tracked
  posts;

  loadPosts() {
    this.postsLoading = true;
    let queryParams = { include: 'reactions,comments' };
    queryParams.groupId = this.model.group.id;
    if (this.selectedPageIds.length >= 1) {
      queryParams.pageIds = JSON.stringify(this.selectedPageIds);
    }
    if (this.selectedAuthorIds.length >= 1) {
      queryParams.authorIds = JSON.stringify(this.selectedAuthorIds);
    }
    if (this.proofreadPosts) {
      queryParams.isProofread = this.proofreadPosts;
    }
    if (this.notProofread) {
      queryParams.isNotProofread = this.notProofread;
    }
    if (this.draftPosts || this.publishedPosts) {
      let statusArray = [];
      if (this.draftPosts) {
        statusArray.push(PostStatus.DRAFT);
        statusArray.push(PostStatus.UNPUBLISHED);
      }
      if (this.publishedPosts) {
        statusArray.push(PostStatus.PUBLISHED);
      }
      queryParams.visibleToParentsOrLearners = true;
      queryParams.statuses = JSON.stringify(statusArray);
    }
    if (this.staffOnly) {
      queryParams.notVisibleToParents = this.staffOnly;
      queryParams.notVisibleToLearners = this.staffOnly;
    }
    if (this.startDate !== '') {
      queryParams.startTime = moment(this.startDate).startOf('day').unix();
    }
    if (this.endDate !== '') {
      queryParams.endTime = moment(this.endDate).endOf('day').unix();
    }

    this.store.query('posts/post', queryParams).then((posts) => {
      this.posts = posts;
      this.postsLoading = false;
    });
  }

  clearFiltersImpl() {
    this.proofreadPosts = false;
    this.notProofread = false;
    this.draftPosts = false;
    this.publishedPosts = false;
    this.staffOnly = false;
    this.startDate = moment().startOf('year').format('YYYY-MM-DD');
    this.endDate = moment().endOf('year').format('YYYY-MM-DD');
  }

  @action
  toggleProofread() {
    if (!this.proofreadPosts && this.notProofread) {
      this.notProofread = false;
    }
    this.proofreadPosts = !this.proofreadPosts;
  }

  @action
  toggleNotProofread() {
    if (!this.notProofread && this.proofreadPosts) {
      this.proofreadPosts = false;
    }
    this.notProofread = !this.notProofread;
  }

  @action
  toggleInDraft() {
    if (!this.draftPosts && this.staffOnly) {
      this.staffOnly = false;
    }
    this.draftPosts = !this.draftPosts;
  }

  @action
  toggleIsPublished() {
    if (!this.publishedPosts && this.staffOnly) {
      this.staffOnly = false;
    }
    this.publishedPosts = !this.publishedPosts;
  }

  @action
  toggleVisibility() {
    if (!this.staffOnly && (this.draftPosts || this.publishedPosts)) {
      this.draftPosts = false;
      this.publishedPosts = false;
    }
    this.staffOnly = !this.staffOnly;
  }

  @action
  togglePage(page) {
    if (this.selectedPages.includes(page)) {
      this.selectedPageIds = this.selectedPageIds.filter(
        (id) => id !== page.id,
      );
      return;
    }
    if (this.selectedPages.length >= 5) {
      return this.toaster.error(
        'Filtering has a maximum of 5 pages. Please remove one before adding more.',
      );
    }
    this.selectedPageIds = [...this.selectedPageIds, page.id];
  }

  @action
  toggleAuthor(author) {
    if (this.selectedAuthors.includes(author)) {
      this.selectedAuthorIds = this.selectedAuthorIds.filter(
        (id) => id !== author.id,
      );
      return;
    }
    if (this.selectedAuthors.length >= 5) {
      return this.toaster.error(
        'Filtering has a maximum of 5 authors. Please remove one before adding more.',
      );
    }
    this.selectedAuthorIds = [...this.selectedAuthorIds, author.id];
  }

  @action
  autoFilter(param) {
    switch (param) {
      case 'day':
        if (this.dateRange !== 'day') {
          this.startDate = moment().format('YYYY-MM-DD');
          this.endDate = moment().format('YYYY-MM-DD');
        }
        break;
      case 'week':
        if (this.dateRange !== 'week') {
          this.startDate = moment()
            .subtract(7, 'days')
            .startOf('day')
            .format('YYYY-MM-DD');
          this.endDate = moment().endOf('day').format('YYYY-MM-DD');
        }
        break;
      case 'term':
        if (this.dateRange !== 'term') {
          this.startDate = this.get('termDates.startDate');
          this.endDate = this.get('termDates.endDate');
        }
        break;
    }
  }

  @action
  setDate(param, date) {
    this.set(param, date);
  }

  @action
  clearFilters() {
    this.startDate = moment().startOf('year').format('YYYY-MM-DD');
    this.endDate = moment().endOf('year').format('YYYY-MM-DD');
  }

  @action
  proofread(post) {
    post.set('proofreaderId', this.session.person);
    post.set('proofreadTime', moment().unix());
    return this.saveProofread(post);
  }

  @action
  unproofread(post) {
    post.set('proofreaderId', null);
    post.set('proofreadTime', 0);
    return this.saveProofread(post);
  }

  // saveProofread saves a proofread change, putting the post back if the
  // save fails; rollbackAttributes() alone would leave proofreaderId.
  saveProofread(post) {
    return post.save().catch(() => {
      rollbackRecord(post);
      this.toaster.error('Failed to update the post. Please try again.');
    });
  }

  @action
  reloadPosts() {
    this.loadPosts();
  }

  @action
  transitionToBulkPublishing() {
    let params = {
      groupId: this.groupId,
      proofreadPosts: this.proofreadPosts,
      notProofread: this.notProofread,
      pageIds: JSON.stringify(this.selectedPageIds),
      authorIds: JSON.stringify(this.selectedAuthorIds),
      startDate: this.startDate,
      endDate: this.endDate,
      draftPosts: this.draftPosts,
      publishedPosts: this.publishedPosts,
      staffOnly: this.staffOnly,
    };
    this.router.transitionTo('group.bulk-publish', { queryParams: params });
  }
}
